import posthog from 'posthog-js';

/**
 * PostHog Analytics Service
 *
 * Privacy-first session recording configuration:
 * - Records chat interactions (user messages and AI responses)
 * - Masks all other inputs (login, payment, profile, job search)
 * - Blocks sensitive forms completely
 * - Excludes authentication and payment network requests
 */

export interface PostHogConfig {
  enabled: boolean;
  apiKey: string;
  host: string;
}

export const posthogConfig: PostHogConfig = {
  enabled: process.env.REACT_APP_POSTHOG_ENABLED === 'true',
  apiKey: process.env.REACT_APP_POSTHOG_KEY || '',
  host: process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com',
};

/**
 * PostHog initialization options with privacy-focused session recording
 */
export const posthogOptions: any = {
  api_host: posthogConfig.host,

  // Session recording configuration
  session_recording: {
    // IMPORTANT: Explicitly enable session recording
    enabled: true,

    // Mask all inputs by default for privacy
    maskAllInputs: true,

    // Mask all text content by default
    maskAllText: false, // Set to false to allow chat messages to be visible

    // Completely block these elements from recording
    blockClass: 'ph-no-capture',
    blockSelector: [
      // Password fields
      'input[type="password"]',
      // Email inputs (login forms)
      'input[type="email"]',
      'input[name*="email"]',
      // Payment-related inputs
      'input[name*="card"]',
      'input[name*="cvv"]',
      'input[name*="cvc"]',
      'input[name*="expiry"]',
      // Form containers
      '.login-form',
      '.auth-form',
      '.payment-form',
      '[data-payment]',
    ].join(', '),

    // Don't mask these elements (chat-specific)
    unmaskTextSelector: [
      '.chat-content',        // Chat message content
      '.chat-message',        // Chat message container
      '[data-chat-message]',  // Chat message elements
    ].join(', '),

    // Network request recording settings
    recordCrossOriginIframes: false,

    // Record console logs for debugging
    recordConsoleErrors: true,

    // Mask input selectively based on context
    maskInputFn: (text: string, element?: HTMLElement) => {
      if (!element) return text;

      // Don't mask chat input textarea
      if (element.classList.contains('chat-input') ||
          element.closest('.chat-input-container')) {
        return text;
      }

      // Mask everything else
      return text.replace(/./g, '*');
    },
  },

  // Capture settings
  autocapture: true,
  capture_pageview: true,
  capture_pageleave: true,

  // Advanced privacy settings
  property_blacklist: [
    '$password',
    '$email',
    '$card_number',
    '$cvv',
  ],

  // Respect Do Not Track
  respect_dnt: true,

  // Persistence
  persistence: 'localStorage',

  // Performance
  loaded: (posthogInstance: any) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ PostHog loaded successfully');
      console.log('📹 Session recording enabled:', posthogInstance.sessionRecording?.started);
      console.log('🔑 PostHog API Key:', posthogConfig.apiKey.substring(0, 20) + '...');
      console.log('🌐 PostHog Host:', posthogConfig.host);
    }
  },
};

/**
 * PostHog Service
 * Provides methods to interact with PostHog analytics
 */
export const posthogService = {
  /**
   * Check if PostHog is enabled and initialized
   */
  isEnabled(): boolean {
    return posthogConfig.enabled && posthogConfig.apiKey !== '';
  },

  /**
   * Initialize PostHog
   * Called automatically when the app starts via PostHogProvider
   */
  initialize(): void {
    if (!this.isEnabled()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('PostHog is disabled');
      }
      return;
    }

    try {
      posthog.init(posthogConfig.apiKey, posthogOptions);

      if (process.env.NODE_ENV === 'development') {
        console.log('PostHog initialized with config:', {
          host: posthogConfig.host,
          sessionRecording: true,
        });
      }
    } catch (error) {
      console.error('Failed to initialize PostHog:', error);
    }
  },

  /**
   * Identify a user after login
   * @param email - User's email address (used as unique identifier)
   * @param properties - Additional user properties
   */
  identifyUser(email: string, properties?: Record<string, any>): void {
    if (!this.isEnabled()) return;

    try {
      posthog.identify(email, {
        email,
        ...properties,
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('👤 PostHog user identified:', email);
        console.log('📹 Session recording status:', posthog.sessionRecording?.started ? 'Recording' : 'Not recording');
      }
    } catch (error) {
      console.error('Failed to identify user in PostHog:', error);
    }
  },

  /**
   * Reset user identity on logout
   * Clears user data and starts a new session
   */
  reset(): void {
    if (!this.isEnabled()) return;

    try {
      posthog.reset();

      if (process.env.NODE_ENV === 'development') {
        console.log('PostHog user identity reset');
      }
    } catch (error) {
      console.error('Failed to reset PostHog:', error);
    }
  },

  /**
   * Get the PostHog instance
   * Use this for advanced features
   */
  getInstance(): typeof posthog {
    return posthog;
  },
};

export default posthogService;
